/**
 * JOProClickCollect — Featured B2B section promoting a dedicated
 * Click & Collect service for landscape professionals.
 *
 * Rendered only in the Paysagiste universe (or forced via `forceShow` prop
 * when injected at the top of the /services page).
 *
 * Non-core, fully additive. Styles in jardins-ole.css under `.jo-procc-*`.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Clock, Truck, BadgeCheck, ArrowRight, Sparkles } from "lucide-react";
import { useUniverse } from "@/lib/jardins-ole/useUniverse";
import { useSettings } from "@/lib/useSettings";

const BENEFITS = [
  {
    Icon: Clock,
    title: "Gain de temps",
    text: "Commande passée le matin, retrait en boutique en 2 heures ouvrées. Plus d'attente aux comptoirs.",
  },
  {
    Icon: Truck,
    title: "Logistique optimisée",
    text: "Vos commandes regroupées et préparées par nos équipes : un seul arrêt, tous vos végétaux prêts.",
  },
  {
    Icon: BadgeCheck,
    title: "Tarifs pro dédiés",
    text: "Compte professionnel avec remise sur l'ensemble du catalogue et conditions de paiement adaptées.",
  },
];

export default function JOProClickCollect({ forceShow = false, compact = false }) {
  const { universe } = useUniverse();
  const { settings: s } = useSettings();
  // Only surface in Paysagiste universe unless forceShow (e.g. /services injector)
  if (!forceShow && universe !== "paysagiste") return null;

  const phone = s?.universe_paysagiste?.phone || s?.phone;

  return (
    <section
      className={`jo-procc ${compact ? "jo-procc--compact" : ""}`}
      data-testid="jo-pro-clickcollect"
    >
      <div className="jo-procc-bg" aria-hidden="true" />
      <div className="jo-container jo-procc-inner">
        <div className="jo-procc-head">
          <span className="jo-procc-tag">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} />
            Service réservé aux professionnels
          </span>
          <h2 className="jo-procc-title">
            Click&nbsp;&amp;&nbsp;Collect&nbsp;<em>Pro</em><br/>
            <span className="jo-procc-subline">pour les paysagistes &amp; entreprises d'aménagement</span>
          </h2>
          <p className="jo-procc-lead">
            Services aux professionnels de l'aménagement paysager : bénéficiez d'un{" "}
            <strong>Click&amp;Collect dédié</strong> pour gagner du temps et en efficacité
            sur vos chantiers.
          </p>
        </div>

        <div className="jo-procc-grid">
          {BENEFITS.map((b, i) => {
            const Icon = b.Icon;
            return (
              <div className="jo-procc-card" key={i} data-testid={`jo-procc-benefit-${i}`}>
                <div className="jo-procc-icon">
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <h3 className="jo-procc-card-title">{b.title}</h3>
                <p className="jo-procc-card-text">{b.text}</p>
              </div>
            );
          })}
        </div>

        <div className="jo-procc-cta">
          <Link to="/contact?type=pro-clickcollect" className="jo-procc-btn-primary" data-testid="jo-procc-cta-contact">
            <ShoppingBag className="w-4 h-4" strokeWidth={2.4} />
            <span>Ouvrir un compte pro</span>
            <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </Link>
          {phone && (
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="jo-procc-btn-secondary" data-testid="jo-procc-cta-phone">
              Ou appelez-nous : <strong>{phone}</strong>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
